import React, { useCallback } from "react"
import { useFormContext } from "react-hook-form"
import MuiTextField from "@mui/material/TextField"

import { FormField } from "./types"
import {
  cleanUpMoneyRangeValue,
  getFormattedMoney,
  getFormattedMoneyRange,
  getFormattedPhoneNumber,
  preventSubmitOnEnter,
  trackFormEvent,
} from "./utils"
import { WithParticleOverlay } from "./WithParticleOverlay"

export interface Props extends FormField {
  endAdornment?: React.ReactNode
  defaultValue?: string
  multiline?: boolean
  startAdornment?: React.ReactNode
  formatting?: "phone" | "money" | "money-range" // add more if needed
  helperText?: React.ReactNode
  type?: React.HTMLInputTypeAttribute
  placeholder?: string
  hideErrorLabel?: boolean
  autoFocus?: boolean
  classes?: {
    root?: string
    input?: string
    label?: string
  }
  maxRows?: number
  rows?: number
  error?: string

  onBlur?: () => void
  onFocus?: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
}

const TextField = ({
  className,
  defaultValue,
  endAdornment,
  label,
  name,
  multiline,
  rules,
  startAdornment,
  formatting,
  disabled,
  loading,
  helperText,
  type,
  placeholder,
  onBlur,
  onFocus,
  onKeyDown,
  hideErrorLabel,
  autoFocus,
  classes,
  tracking,
  maxRows,
  rows,
  error: error_,
}: Props) => {
  const { register, formState } = useFormContext()
  const { onChange, ...registration } = register(name, rules)

  const error = error_ ?? formState.errors[name]?.message?.toString()
  const errorProps = error ? { error: true, label: !hideErrorLabel && error } : {}

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      switch (formatting) {
        case "phone":
          e.target.value = getFormattedPhoneNumber(e.target.value) || e.target.value
          break
        case "money":
          e.target.value = getFormattedMoney(e.target.value)
          break
        case "money-range":
          e.target.value = getFormattedMoneyRange(e.target.value)
          break
        default:
          break
      }

      await onChange(e)
    },
    [onChange, formatting]
  )

  const handleBlur = useCallback(
    async (e: React.FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      switch (formatting) {
        case "money-range":
          e.target.value = cleanUpMoneyRangeValue(e.target.value)
          break
        default:
          break
      }

      onBlur?.()
      await registration.onBlur(e)

      if (tracking?.shouldTrackValueChange && e.target.value.trim()) {
        await trackFormEvent("Form Value Provided", tracking, name, label)
      }
    },
    [formatting, label, name, onBlur, registration, tracking]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!multiline) preventSubmitOnEnter(e)

      onKeyDown?.(e)
    },
    [multiline, onKeyDown]
  )

  return (
    <WithParticleOverlay id={name} loading={loading} classes={{ root: className }}>
      <div>
        <MuiTextField
          defaultValue={defaultValue}
          // key={defaultValue}
          disabled={disabled}
          InputProps={{
            endAdornment,
            startAdornment,
            classes,
          }}
          InputLabelProps={{ classes: { root: classes?.label } }}
          label={label}
          placeholder={placeholder}
          multiline={multiline}
          onChange={handleChange}
          helperText={helperText}
          onKeyDown={handleKeyDown}
          type={type}
          {...registration}
          {...errorProps}
          onBlur={handleBlur}
          onFocus={onFocus}
          autoFocus={autoFocus}
          maxRows={maxRows}
          rows={rows}
        />
      </div>
    </WithParticleOverlay>
  )
}

export default TextField
